import {
	db,
	DBType,
	DEBUG,
	IEntityRest,
	IErrorRestResponse,
	IRestCollectionParams,
	IRestDeleteParams,
	IRestParams,
	IRestResult,
	IVIPCertificate,
	IVIPCertificateOptions,
	RestAPI,
	RestCollectionParam,
	staticImplements,
	toaster,
	VIPCertificate,
	VIPCertificateFields,
} from "@/loader";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";

export interface IVIPCertificatePdf {
	signed: boolean;
	content: string;
}

export interface IVIPCertificateRestGetParams extends IRestParams {
	options?: IVIPCertificateOptions;
}

export interface IVIPCertificateRestCollectionParams extends IRestCollectionParams, IVIPCertificateRestGetParams  {
	orderby?: VIPCertificateFields | VIPCertificateFields[];
	id?: RestCollectionParam<number>;
	fk_project?: RestCollectionParam<number>;
	date?: RestCollectionParam<string>;
	status?: RestCollectionParam<string>;
	type?: RestCollectionParam<string>;
	data?: RestCollectionParam<string>;
	fk_ip?: RestCollectionParam<number>;
}

@staticImplements<IEntityRest<IVIPCertificate, VIPCertificate>>()
export abstract class VIPCertificateRest {
	public static readonly endpoint: string = "certificates";

	public static getURL(data?: IVIPCertificate, singular: boolean = false): string {
		let url: string[] = [RestAPI.url.replace(/\/$/, ""), RestAPI.namespace, VIPCertificateRest.endpoint];
		if (singular && data?.id) {
			url.push(data.id.toString());
		}
		return url.join("/");
	}

	public static getPdfURL(id?: number): string {
		if (!id || Number.isNaN(id)) {
			throw new Error("The certificate must have a valid ID");
		}
		let url: string[] = [RestAPI.url.replace(/\/$/, ""), RestAPI.namespace, VIPCertificateRest.endpoint, id.toString(), "pdf"];
		return url.join("/");
	}

	public static getStatusURL(id?: number): string {
		if (!id || Number.isNaN(id)) {
			throw new Error("The certificate must have a valid ID");
		}
		let url: string[] = [RestAPI.url.replace(/\/$/, ""), RestAPI.namespace, VIPCertificateRest.endpoint, id.toString(), "status"];
		return url.join("/");
	}

	public static async list(params?: IVIPCertificateRestCollectionParams): Promise<IRestResult<VIPCertificate>> {
		return await RestAPI.list<IVIPCertificate, VIPCertificate>(VIPCertificateRest.getURL(), DBType.certificates, params);
	}

	public static async get(data: IVIPCertificate, params?: IVIPCertificateRestGetParams): Promise<IRestResult<VIPCertificate>> {
		return await RestAPI.get<IVIPCertificate, VIPCertificate>(VIPCertificateRest.getURL(data, true), DBType.certificates, params);
	}

	public static async create(data: IVIPCertificate): Promise<IRestResult<VIPCertificate>> {
		return await RestAPI.post<IVIPCertificate, VIPCertificate>(VIPCertificateRest.getURL(data), data, DBType.certificates);
	}

	public static async update(data: IVIPCertificate): Promise<IRestResult<VIPCertificate>> {
		return await RestAPI.post<IVIPCertificate, VIPCertificate>(VIPCertificateRest.getURL(data, true), data, DBType.certificates);
	}

	public static async delete(data: IVIPCertificate, params?: IRestDeleteParams): Promise<IRestResult<VIPCertificate>> {
		return await RestAPI.delete<IVIPCertificate, VIPCertificate>(VIPCertificateRest.getURL(data, true), DBType.certificates, params);
	}

	public static async changeStatus(certificate: VIPCertificate): Promise<IRestResult<IVIPCertificate>> {
		return await axios.post<IVIPCertificate>(
			VIPCertificateRest.getStatusURL(certificate.id),
			{
				status: certificate.status,
				date: certificate.date?.format("YYYY-MM-DD") || undefined,
				event: certificate.event,
				options: { events: { event: true } }
			}
		).then(response => {
			let item = db.certificates.insertOrUpdate(response.data);
			return { success: true, item };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			toaster.toast({ message: error.response?.data.message || "Une erreur inconnue est survenue.", variant: "danger" });
			return { success: false, error: error.response?.data };
		});
	}

	public static async getPDF(certificate: IVIPCertificate, signed: boolean): Promise<IRestResult<IVIPCertificatePdf>> {
		return await axios.get<IVIPCertificatePdf>(VIPCertificateRest.getPdfURL(certificate.id), { params: { signed } }).then(response => {
			return { success: true, item: response.data };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			if (error.response?.data.code !== "file_not_found") {
				toaster.toast({ message: error.response?.data.message || "", variant: "danger" });
			}
			return { success: false, error: error.response?.data };
		});
	}

	public static async generatePDF(certificate: IVIPCertificate): Promise<IRestResult<IVIPCertificatePdf>> {
		return await axios.put<IVIPCertificatePdf>(VIPCertificateRest.getPdfURL(certificate.id)).then(response => {
			return { success: true, item: response.data };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			toaster.toast({ message: error.response?.data.message || "", variant: "danger" });
			return { success: false, error: error.response?.data };
		});
	}

	public static async uploadPDF(certificate: VIPCertificate, file: File): Promise<IRestResult<VIPCertificate>> {
		let formData = new FormData();
		formData.append("file", file);
		formData.append("date", (certificate.date || dayjs()).format("YYYY-MM-DD"));
		formData.append("event[date]", certificate.event?.date || dayjs().format("YYYY-MM-DD HH:mm:ss"));
		formData.append("event[description]", certificate.event?.description || "");

		return await axios.post<IVIPCertificate>(VIPCertificateRest.getPdfURL(certificate.id), formData, { headers: { "Content-Type": "multipart/form-data" } }).then(response => {
			certificate.update(response.data);
			return { success: true, item: certificate };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			toaster.toast({ message: error.response?.data.message || "", variant: "danger" });
			return { success: false, error: error.response?.data };
		});
	}

	public static async deletePDF(certificate: IVIPCertificate, signed: boolean): Promise<IRestResult<any>> {
		return await axios.delete<any>(VIPCertificateRest.getPdfURL(certificate.id), { data: { signed } }).then(response => {
			return { success: true };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			toaster.toast({ message: error.response?.data.message || "", variant: "danger" });
			return { success: false, error: error.response?.data };
		});
	}
}
